import {
  CABINET_BATHROOM_LABELS,
  CABINET_BOXES,
  CABINET_PART_IDS,
  SHAPE_PART_ID,
  SQFT_APPLIANCES,
  WALL_MOUNTED_CABINET_BOX_PART_IDS,
} from "../constants";
import store from "../../../store";
import workspace from "../components/canvas/core/workspace";
import sqftShape from "../components/canvas/core/shapes/sqftShape";
import {getCurrentLayout} from "../../appLayout";
import MovableImage from "../components/canvas/core/shapes/movableImage";
import {
  containsHEBeforeExtension,
  findGLBLink,
  get3dURLsForFeature,
  getCategoryFromMetadata,
  getShapePointsOnZeroRotation,
  getShapeDbFeature,
  getShape3dModelUrl,
  getThirdPartyAR3DMetaData,
  getUrlMatchingByColor,
  isEnabledColorForRenuitAndHomeDepot,
  isHoodShape,
  wasShapeResized,
} from "./sqft3dArHelper";
import workspaceControllers from "../components/canvas/store/workspaceControllers";
import sqftShapeManager from "../components/canvas/store/sqftShapeManager";
import {featureThumbnailUrl} from "../../../misc/helpers";
import {checkIfLookFeaturesReceived} from "../../../api/look-api";
import SqftJsonApi from "../api/sqftJsonApi";
import {isShapeTallCabinet} from "./cabinetBoxCompatibilityHelper";
// import Misc from "../../../misc/misc";
// import {isTouchDevice} from "../../../misc/utils";

export const DEFAULT_COUNTERTOP_HEIGHT = 34;
export const COUNTERTOP_THICKNESS = 0.5;
export const MAXIMUM_DEFAULT_BOTTOM_CABINET_HEIGHT = 40;
export const MAXIMUM_DEFAULT_TOP_CABINET_HEIGHT = 61;
export const DEFAULT_BACKSPLASH_HEIGHT = 18;
export const MIN_CABINET_HOOD_HEIGHT = 30;

export class CabinetsSet {

  /**
   * NOT USED
   * @param displayDoorsOnly
   * @param shouldDisplayFramedDoors
   * @return {{doors_only: boolean, positions, should_display_framed_doors: boolean}}
   */
  static async prepareCabinetsSet(displayDoorsOnly = false, shouldDisplayFramedDoors = false) {
    const cabinetPositions = await CabinetsSet.prepareCabinetsPositions();
    const countertopPositions = CountertopPositions.prepareCountertopPositions();
    const wallPositions = WallPositions.prepareWallPositions();
    const cabinetSet = {
      "doors_only": displayDoorsOnly,
      "should_display_framed_doors": shouldDisplayFramedDoors,
      "positions": [...cabinetPositions, ...countertopPositions, ...wallPositions]
    }

    // add countertop if bottom_cabinets exist
    // if (cabinetSet.positions.some(position => position.partId === SHAPE_PART_ID.lower_cabinet_boxes)) {
    //   let texture = "https://static6.depositphotos.com/1031174/647/i/450/depositphotos_6477080-stock-photo-grungy-background.jpg";
    //   const state = store.getState();
    //   if (state.sqftState.selectedCountertop) {
    //     const layout = getCurrentLayout();
    //     const countertopFeature = layout.getFeatureById(state.sqftState.selectedCountertop, true);
    //     if (countertopFeature && countertopFeature.dbFeature.thirdPartyAR3DUrls) {
    //       const URLByFeatureName = get3dURLsForFeature(countertopFeature);
    //       texture = URLByFeatureName ? URLByFeatureName : countertopFeature.dbFeature.thirdPartyAR3DUrls[0]
    //     }
    //   }
    //
    //   cabinetSet.countertop = {
    //     "main": "https://quotecountertops.com/video/sqft/cabinets/Quarter Round_Main Body.glb",
    //     "edge": "https://quotecountertops.com/video/sqft/cabinets/Quarter Round_Edge.glb",
    //     "corner": "https://quotecountertops.com/video/sqft/cabinets/Quarter Round_Corner.glb",
    //     "texture": texture
    //   }
    // }

    return cabinetSet
  }

  static async prepareCabinetsPositions(shouldDisplayFramedDoors = false) {
    const layout = getCurrentLayout();
    const shapes = workspace.getShapes();

    const results = [];
    for (const shape of shapes) {
      if (shape instanceof sqftShape) {
        let absolutePos = shape.shapeAbsolute.getAbsolutePosition();
        let shapePosition = absolutePos ? absolutePos : shape.getPosition();

        //disable cabinets if they are not in the list (for renuit and home depot)
        const isEnabledColor = isEnabledColorForRenuitAndHomeDepot(shape.relatedParentFeatureId);
        const relatedParentFeature = shape.getRelatedParentFeature();

        if (relatedParentFeature && isEnabledColor) {
          const shapeDbFeature = getShapeDbFeature(shape);
          results.push(CabinetsSet.prepareRegularCabinetSet(shape, shapePosition, shapeDbFeature, relatedParentFeature, shouldDisplayFramedDoors));
        } else {
          console.log("3d model cannot be prepared, its parent not found shape.relatedParentFeatureId:", shape.relatedParentFeatureId);
        }
      } else if (shape instanceof MovableImage) {
        let absolutePos = shape.shapeAbsolute.getAbsolutePosition();
        const shapePosition = absolutePos ? absolutePos : shape.getPosition();
        const featureGroup = layout.getFeatureGroupByPartId(shape.subType);
        if (featureGroup) {
          if (!featureGroup.allFeaturesReceived && !featureGroup.requestInProgress) {
            await checkIfLookFeaturesReceived(featureGroup);
          }
          const shapeFeature = layout.getFeatureById(shape.featureId);

          if (shapeFeature) {
            results.push(CabinetsSet.prepareImageCabinetSet(shape, shapePosition, shapeFeature));
          }
        }
      }
    }

    return results.filter(cabinetSet => cabinetSet !== null);
  }

  /**
   * Adjust model position based on shape position, dimensions, and rotation.
   * @returns {Object} Updated model position.
   * @param shapePosition
   * @param shapeWidth
   * @param shapeDepth
   * @param rotatedDegrees
   */
  static adjustApplianceModelPosition(shapePosition, shapeWidth, shapeDepth, rotatedDegrees) {
    // Convert degrees to radians and account for the model's initial -90 degree rotation offset
    const radians = (Math.PI / 180) * (rotatedDegrees - 90);

    // Calculate the center of the shape, used as the base point for rotation
    const centerX = shapePosition.x + shapeWidth / 2;
    const centerZ = shapePosition.y + shapeDepth / 2;

    // Calculate the offsets for X and Z axes based on the current rotation angle
    const offsetX = (shapeWidth / 2) * Math.cos(radians);
    const offsetZ = (shapeDepth / 2) * Math.sin(radians);

    // Apply Z-axis correction dynamically based on the sine of the rotation angle
    const zCorrection = shapeDepth * Math.sin(radians);
    const xCorrection = (shapeWidth - shapeDepth) / 2 * Math.cos(radians); //7

    // Return the updated position of the model
    return {
      x: centerX - offsetX + xCorrection, // Corrected X position after rotation
      y: shapePosition.y,                 // Y position remains unchanged (fixed height)
      z: centerZ - offsetZ + zCorrection, // Corrected Z position with dynamic adjustment
      rotatedDegrees: rotatedDegrees - 90
    };
  }

  /**
   * This function corrects shape position because shape rotation in sqft is made around shape center
   * while baking models script rotates shapes around top left corner, so we need to move shape a bit.
   * @param rotatedDegrees
   * @param position
   * @param width
   * @param height
   * @return {{x: *, y: *}}
   */
  static getShapeCorrectedPos = (rotatedDegrees, position, width, height) => {
    // get radians
    let thetaRad = -(rotatedDegrees * Math.PI / 180);

    // left top corner pos (shape pos)
    let xInit = position.x;
    let yInit = position.y;

    // shape center after rotation
    let xCenterBAfterRotation = xInit + width / 2 * Math.cos(thetaRad) - height / 2 * Math.sin(thetaRad);
    let yCenterBAfterRotation = yInit + width / 2 * Math.sin(thetaRad) + height / 2 * Math.cos(thetaRad);

    // initial shape center
    let xCenterBInitial = xInit + width / 2;
    let yCenterBInitial = yInit + height / 2;

    // shape center - shape center after rotation
    let deltaX = xCenterBInitial - xCenterBAfterRotation;
    let deltaY = yCenterBInitial - yCenterBAfterRotation;

    return {
      x: rotatedDegrees > 0
        ? position.x + deltaX
        : position.x,
      y: rotatedDegrees > 0
        ? position.y + deltaY
        : position.y
    }
  }

  static prepareRegularCabinetSet = (shape, shapePosition, shapeDbFeature, relatedParentFeature, shouldDisplayFramedDoors) => {
    if (shapeDbFeature) {
      //console.log("shapeDbFeatureFeature.thirdPartyAR3DUrls", shapeDbFeature.thirdPartyAR3DUrls);
    }

    if (!shapeDbFeature || !shapeDbFeature.thirdPartyAR3DUrls) {
      return null
    }

    const shapeWidth = shape.getInitialWidth();
    //if it is cube then we can set target width and resize it
    const targetWidth = shape.getLines().length === 4 ? shape.getSize().width : shape.getWidth();
    const shapeDepth = shape.getInitialDepth();
    const targetDepth = shape.getLines().length === 4 ? shape.getSize().depth : shape.getDepth();
    const shapeHeight = shape.getInitialHeight();
    const targetHeight = shape.shapeCabinet ? shape.getHeight() : shape.getHeight();

    //We are sending top left corner position.
    const originPointOnWorkspace = shape.getOriginPointOnWorkspace();
    const url = getShape3dModelUrl(shape);
    const material_url = getUrlMatchingByColor(relatedParentFeature.dbFeature, relatedParentFeature); //todo remove
    const posY = CabinetsHelper.getCabinetPosY(shape, shapeDbFeature, url, targetHeight);
    const shapeLayout = shape.shapeParams.shapeLayout;
    const threeDPos = { x: originPointOnWorkspace.x, y: posY, z: originPointOnWorkspace.y };
    const handles = CabinetsHelper.getHandles(shape, threeDPos, shape.rotatedDegrees, targetWidth, targetDepth, targetHeight);
    const wasResized = wasShapeResized(shape);
    const pointsOnZeroRotation = getShapePointsOnZeroRotation(shape);

    // log parent feature material
    // if (!material_url) {
    //   console.log("material wasn't found for the feature", shapeDbFeature.id);
    //   console.log("relatedParentFeature.dbFeature.id", relatedParentFeature.dbFeature.id);
    //   console.log("relatedParentFeature.dbFeature.thirdPartyAR3DUrls", relatedParentFeature.dbFeature.thirdPartyAR3DUrls);
    // }

    return {
      "shapeId": shape.id,
      "partId": shape.parentPartId ? shape.parentPartId : shape.partId,
      "is_filler": shape.shapeParams.isSpacer,
      "is_spacer": shape.shapeParams.isSpacer,
      "x": threeDPos.x,
      "y": threeDPos.y, // height between floor and roof. 0 is floor
      "z": threeDPos.z,
      "rotation": shape.rotatedDegrees,
      "url": url,
      "material_url": material_url,
      //"url": "https://master_new_1512.quoteremodel.com/content/Views/visualizers/images/sqft-icons/3d/cabinet.glb",
      "framed": shouldDisplayFramedDoors,
      "frame_width": 0.75,
      "frame_height": 0.25,
      "target_width": targetWidth,
      "target_depth": targetDepth,
      "target_height": targetHeight,
      "width": shapeWidth,
      "height": shapeHeight,
      "depth": shapeDepth,
      "was_resized": wasResized,
      "points_on_zero_rotation": pointsOnZeroRotation,
      "contour_points": shape.getPointsCoords(),
      "original_points": shape.getOriginalPointsCoords(),
      "featureId": shapeDbFeature.id,
      "parentFeatureId": relatedParentFeature.dbFeature.id,
      "shapeLayout": shapeLayout,
      "handles": handles,
      // "url": shapeDbFeatureFeature && shapeDbFeatureFeature.thirdPartyAR3DUrls
      //   ? shapeDbFeatureFeature.thirdPartyAR3DUrls[0]
      //   : "https://qc-3dfiles.s3.amazonaws.com/ar-composer/W3030.glb" //localGlb
      //: "https://dv84u4f1bblk2.cloudfront.net/6cp14aef9e7rfg6gds5orh1cej7ctlvq.glb"
    }
  }

  static prepareImageCabinetSet = (shape, shapePosition, shapeFeature) => {
    if (!shapeFeature || !shapeFeature.dbFeature || !shapeFeature.dbFeature.thirdPartyAR3DUrls) {
      return null
    }
    //console.log("shapeDbFeatureFeature.thirdPartyAR3DUrls", shapeFeature.dbFeature.thirdPartyAR3DUrls);

    const shapeWidth = +shape.getWidth();
    const shapeHeight = +shape.getHeight();
    const shapeDepth = +shape.getDepth();
    let appliancePosition = CabinetsSet.adjustApplianceModelPosition(shapePosition, shapeWidth, shapeDepth, shape.rotatedDegrees);

    const url = findGLBLink(shapeFeature.dbFeature.thirdPartyAR3DUrls);

    //rise hood to the level of top cabinets
    const yPos = shapeFeature.partId === SQFT_APPLIANCES.hood ? DEFAULT_COUNTERTOP_HEIGHT + DEFAULT_BACKSPLASH_HEIGHT : 0;

    // hardcode to create countertop over the dishwasher
    //todo
    //const partId = shapeFeature.partId === SHAPE_PART_ID.dishwasher ? SHAPE_PART_ID.lower_cabinet_boxes: shapeFeature.partId;
    const partId = shapeFeature.partId;

    // we need to hide the hood behind the top cabinet
    //const zPos = shapeFeature.partId === SHAPE_PART_ID.hood ? appliancePosition.y - 0.5 : appliancePosition.y;

    return {
      "featureId": shapeFeature.dbFeature.id,
      "shapeId": shape.id,
      "partId": partId,
      "x": appliancePosition.x,
      "y": yPos, //todo (shape bottom cabinet height + distance between bottom and top cabinets)
      "z": appliancePosition.z,
      "rotation": appliancePosition.rotatedDegrees,
      "url": url,
      "width": shapeWidth,
      "height": shapeHeight, //in sqft wee cabinets from top so we can see this value
      "depth": shapeDepth,
      "target_height": shapeHeight, //same target height to prevent resizing
    }
  }

  static prepareFrontLines = (partId) => {
    const shapes     = workspace.getShapes();
    const frontLines = shapes
      .filter(shape => shape.partId === partId)
      .map(shape => shape.shapeCabinet.getFrontLinesOnWorkspace())
      .flat();

    console.log("frontLines", frontLines);
    return frontLines
  }

  static prepareCabinetFrontLines = () => {
    const cabinetPartIds = [
      ...CABINET_BOXES,
      ...CABINET_PART_IDS,
      ...Object.keys(CABINET_BATHROOM_LABELS),
    ];

    return workspace.getShapes()
      .filter(shape => (
        shape.shapeCabinet
        && typeof shape.shapeCabinet.getFrontLinesOnWorkspace === 'function'
        && (
          cabinetPartIds.includes(shape.partId)
          || cabinetPartIds.includes(shape.parentPartId)
        )
      ))
      .map(shape => shape.shapeCabinet.getFrontLinesOnWorkspace())
      .flat();
  }
}

export class CountertopPositions {

  static prepareCountertopPositions() {
    const countertopPositions = [];
    const partIdsCoveredByCountertop = [
      SHAPE_PART_ID.lower_cabinet_boxes,
      SHAPE_PART_ID.vanitie_cabinet_boxes,
      SHAPE_PART_ID.vanity_cabinet_boxes,
      SHAPE_PART_ID.bottom_cabinet,
      SHAPE_PART_ID.dishwasher
    ];

    /**
     * This function will search and return texture in thirdPartyAR3DUrls
     * and if it is not found will return large thumbnail
     * @return {string}
     */
    const getCountertopTexture = () => {
      //let textureUrl = "https://qc-3dfiles.s3.amazonaws.com/ar-textures/Absolute White - MSI - Marble - color - 1 - 0.16 - 0.33.png";
      let textureUrl = "https://features-thumbnails.s3-accelerate.amazonaws.com/feature-env_ReBath_c174af44-eb6f-4a48-9f41-19709afff8b0.png";
      const state = store.getState();
      if (state.sqftState.selectedCountertop) {
        const layout = getCurrentLayout();
        const countertopFeature = layout.getFeatureById(state.sqftState.selectedCountertop, true);
        //const isMobile = Misc.isMobile() || isTouchDevice();
        if (countertopFeature) {
          if (countertopFeature.dbFeature.thirdPartyAR3DUrls) {
            const URLByFeatureName = get3dURLsForFeature(countertopFeature);
            textureUrl = URLByFeatureName ? URLByFeatureName : countertopFeature.dbFeature.thirdPartyAR3DUrls[0];
          } else {
            textureUrl = featureThumbnailUrl(countertopFeature);
          }
        }
      }
      return textureUrl;
    };

    const textureUrl = getCountertopTexture();
    const snappedShapeGroups = workspaceControllers.snappedShapeGroups.getResources();

    // Function to group shapes by partId, height, and handle gaps
    const groupShapes = (shapes) => {
      let currentGroup = [];
      let lastHeight = null;
      let lastRotation = null;

      for (const shape of shapes) {
        const shapeHeight = shape.shapeCabinet ? shape.getHeight() : DEFAULT_COUNTERTOP_HEIGHT;
        const isDishwasher = shape.subType === SHAPE_PART_ID.dishwasher;
        const isHeightForTheBottomCabinet = shape.getInitialHeight() < MAXIMUM_DEFAULT_BOTTOM_CABINET_HEIGHT;

        const isValidShape = (partIdsCoveredByCountertop.includes(shape.partId) ||
          partIdsCoveredByCountertop.includes(shape.parentPartId) ||
          isDishwasher
        ) && isHeightForTheBottomCabinet

        //disable cabinets if they are not in the list (for renuit and home depot)
        const isEnabledColor = isEnabledColorForRenuitAndHomeDepot(shape.relatedParentFeatureId);

        if (!isValidShape || (!isEnabledColor && !isDishwasher)) {
          if (currentGroup.length > 0) {
            CountertopPositions.prepareCountertopForGroup(currentGroup, textureUrl, countertopPositions, lastHeight);
            currentGroup = [];
          }
          lastHeight = null;
          continue;
        }

        // Get shape rotation (or 0 if not present)
        const shapeRotation = shape.rotatedDegrees ? shape.rotatedDegrees : 0;

        // If height changed (and it's not dishwasher) OR rotation changed => close group
        if (
          ((shapeHeight !== lastHeight) && !isDishwasher) ||
          (lastRotation !== null && shapeRotation !== lastRotation)
        ) {
          if (currentGroup.length > 0) {
            CountertopPositions.prepareCountertopForGroup(
              currentGroup,
              textureUrl,
              countertopPositions,
              lastHeight,
              lastRotation
            );
            currentGroup = [];
          }
        }

        lastHeight = shapeHeight + COUNTERTOP_THICKNESS;
        lastRotation = shapeRotation;
        currentGroup.push(shape);
      }

      if (currentGroup.length > 0) {
        CountertopPositions.prepareCountertopForGroup(currentGroup, textureUrl, countertopPositions, lastHeight, lastRotation);
      }
    };

    // Prepare countertops for the snapped lower_cabinet_boxes
    for (const snappedShapeGroup of snappedShapeGroups) {
      let shapes = workspaceControllers.snappedShapeGroups.getSnappedShapes(snappedShapeGroup);

      //shapes = shapes.filter(shp => shp.partId !== SHAPE_PART_ID.upper_cabinet_boxes);
      // shapes = shapes.filter(shp =>
      //   partIdsCoveredByCountertop.includes(shp.partId)
      //   || partIdsCoveredByCountertop.includes(shp.parentPartId)
      //   || partIdsCoveredByCountertop.includes(shp.subType)
      // );

      shapes.sort((a, b) => {
        const posA = a.getPosition();
        const posB = b.getPosition();
        const tolerance = 15; // x tolerance for defining "one line"

        if (Math.abs(posA.x - posB.x) <= tolerance) {
          return posA.y - posB.y;
        }

        return posA.x - posB.x;
      });

      if (shapes.length > 0) {
        groupShapes(shapes);
      }
    }

    // Prepare countertops for the single cabinets
    const shapes = workspace.getShapesWithoutAttachedResources();
    const countertopCabinets = shapes.filter(shape => {

      //disable cabinets if they are not in the list (for renuit and home depot)
      const isEnabledColor = isEnabledColorForRenuitAndHomeDepot(shape.relatedParentFeatureId);
      const isHeightForTheBottomCabinet = shape.getInitialHeight() < MAXIMUM_DEFAULT_BOTTOM_CABINET_HEIGHT;
      const isDishwasher = shape.subType === SHAPE_PART_ID.dishwasher;
      const isValidShape = (partIdsCoveredByCountertop.includes(shape.partId)
        || partIdsCoveredByCountertop.includes(shape.parentPartId)
      ) && isEnabledColor && isHeightForTheBottomCabinet

      return isDishwasher || isValidShape
    }

    );

    for (const countertopCabinet of countertopCabinets) {
      if (countertopCabinet.shapeAbsolute.getSnappedShapeGroup() === null) {
        const countertopCabinetHeight = countertopCabinet.shapeCabinet
          ? countertopCabinet.getHeight() + COUNTERTOP_THICKNESS
          : DEFAULT_COUNTERTOP_HEIGHT + COUNTERTOP_THICKNESS;

        // Pass the shape's rotation
        const shapeRotation = countertopCabinet.rotatedDegrees
          ? countertopCabinet.rotatedDegrees
          : 0;

        CountertopPositions.prepareCountertopForGroup(
          [countertopCabinet],
          textureUrl,
          countertopPositions,
          countertopCabinetHeight,
          shapeRotation
        );
      }
    }

    return countertopPositions;
  }

  /**
   * Prepares a single countertop position object and pushes it into
   * countertopPositions array.
   * @param {Array} snappedShapes
   * @param {string} textureUrl
   * @param {Array} countertopPositions
   * @param {number} shapeHeight
   * @param {number} shapeRotation
   */
  static prepareCountertopForGroup = (snappedShapes, textureUrl, countertopPositions, shapeHeight, shapeRotation = 0) => {
    const measurementPoints = sqftShapeManager.getMinMaxShapesPointsOnWorkspace(snappedShapes);
    const contours = sqftShapeManager.getShapesContourPointsOnWorkspace(snappedShapes, true);

    if (contours.length === 0) {
      console.error("contourPoints is empty");
      return;
    }

    for (const contourPoints of contours) {
      const overhang = sqftShapeManager.getShapesOverhangOnWorkspace(contourPoints, snappedShapes);

      if (measurementPoints.length > 0) {
        const width = measurementPoints[1].x - measurementPoints[0].x;
        const height = measurementPoints[2].y - measurementPoints[1].y;
        const contourId = contourPoints
          .map(p => `${Math.round(p.x)}_${Math.round(p.y)}`)
          .join('-');

        countertopPositions.push({
          'id': contourId,
          'partId': 'countertop',
          'x': contourPoints[0].x,
          'y': shapeHeight,
          'z': contourPoints[0].y,
          'width': width,
          'height': height,
          'edge_url': 'https://quotecountertops.com/video/sqft/countertop_edges/Eased Countertop.glb',
          'texture_url': textureUrl,
          'texture_rotation': shapeRotation,
          'contour_points': contourPoints,
          'overhang': overhang
        });
      }
    }
  }

  static prepareCountertopUrls = () => {
    const positions = CountertopPositions.prepareCountertopPositions();
    console.log("countertops", positions);

    return positions.map((pos, idx) => async () => {
      const cp = await SqftJsonApi.getCountertops(pos, {});
      if (!cp.s3Url) {
        console.log(`No URL for countertop at position ${idx}`);
        return;
      }
      return cp.s3Url
    })
  }
}

export class WallPositions {
  static prepareWallPositions() {
    const walls = workspaceControllers.walls.getResources();
    const getWallTexture = () => {
      let textureUrl = "https://qc-3dfiles.s3.amazonaws.com/ar-textures/Absolute White - MSI - Marble - color - 1 - 0.16 - 0.33.png";
      const state = store.getState();
      if (state.sqftState.selectedWall) {
        const layout = getCurrentLayout();
        const wallFeature = layout.getFeatureById(state.sqftState.selectedWall, true);
        if (wallFeature) {
          if (wallFeature.dbFeature.thirdPartyAR3DUrls) {
            const URLByFeatureName = get3dURLsForFeature(wallFeature);
            textureUrl = URLByFeatureName ? URLByFeatureName : wallFeature.dbFeature.thirdPartyAR3DUrls[0];
          } else {
            textureUrl = featureThumbnailUrl(wallFeature);
          }
        }
      }
      return textureUrl;
    };
    
    return walls.map(wall => {
      return {
        "partId": "wall",
        "texture_url": getWallTexture(),
        "height": 100.0,
        "contour_points": wall.getPoints().map(point => point.getPosition())
      }
    })
  }
}

export class CabinetsHelper {
  static getUpperCabinetShapes() {
    const shapes = workspace.getShapesWithoutAttachedResources();
    return shapes.filter(shape => {
      const shapeInitialHeight = shape.getInitialHeight();
      const isValidUpperCabinetHeight = shapeInitialHeight < MAXIMUM_DEFAULT_TOP_CABINET_HEIGHT;

      return WALL_MOUNTED_CABINET_BOX_PART_IDS.includes(shape.partId) && isValidUpperCabinetHeight
    })
  }

  static getTallCabinetShapes() {
    const shapes = workspace.getShapesWithoutAttachedResources();
    return shapes.filter(shape => isShapeTallCabinet(shape));
  }

  static getHighestCabinetValue(shapes) {
    if (shapes.length === 0) {
      return 0;
    }

    return shapes.reduce((acc, current) => {
      let currentShapeHeight = 0;

      if (current instanceof sqftShape || current instanceof MovableImage) {
        currentShapeHeight = current.getHeight();
      }

      if (currentShapeHeight > acc) {
        return currentShapeHeight;
      }

      return acc;
    }, 0);
  }

  static getCabinetPosYFromShape (shape) {
    const shapeDbFeature = getShapeDbFeature(shape);
    const url = getShape3dModelUrl(shape);

    if (shapeDbFeature && url) {
      return CabinetsHelper.getCabinetPosY(shape, shapeDbFeature, url, shape.getHeight())
    }

    return 0
  }

  static getCabinetPosY(shape, shapeDbFeature, url, shapeHeight) {
    let posY = 0
    const metaData = getThirdPartyAR3DMetaData(shapeDbFeature, url);
    const category = getCategoryFromMetadata(metaData);
    const shapeInitialHeight = shape.getInitialHeight();
    const isValidUpperCabinetHeight = shapeInitialHeight < MAXIMUM_DEFAULT_TOP_CABINET_HEIGHT;

    if (isValidUpperCabinetHeight && (
      WALL_MOUNTED_CABINET_BOX_PART_IDS.includes(shape.partId)
      || WALL_MOUNTED_CABINET_BOX_PART_IDS.includes(shape.parentPartId)
    )) {
      // //todo (shape bottom cabinet height + distance between bottom and top cabinets)
      posY = DEFAULT_COUNTERTOP_HEIGHT + DEFAULT_BACKSPLASH_HEIGHT;

      //set hood cabinet height to the level of highest upper_cabinet value
      const isHoodCabinet = category === "Hood" || containsHEBeforeExtension(url) || shapeHeight <= DEFAULT_BACKSPLASH_HEIGHT;
      if (isHoodCabinet) {
        const snappedWithShape = shape.shapeAbsolute.getSnappedWithShape();

        //const snappedShapes = workspaceControllers.snappedShapeGroups.getSnappedShapes(shape.shapeAbsolute.snappedShapeGroup);
         let upperCabinetHighestCabinetValue;
        // todo might be issues here, it is better to search for the closest cabinet
        if (snappedWithShape
          && !isHoodShape(snappedWithShape)
          && WALL_MOUNTED_CABINET_BOX_PART_IDS.includes(snappedWithShape.partId)) {
          upperCabinetHighestCabinetValue = Math.max(MIN_CABINET_HOOD_HEIGHT, parseFloat(snappedWithShape.getHeight()));
        } else {
          const upperCabinetShapes = CabinetsHelper.getUpperCabinetShapes();
          upperCabinetHighestCabinetValue = Math.max(MIN_CABINET_HOOD_HEIGHT, parseFloat(CabinetsHelper.getHighestCabinetValue(upperCabinetShapes)));
        }
        posY = DEFAULT_COUNTERTOP_HEIGHT + DEFAULT_BACKSPLASH_HEIGHT + upperCabinetHighestCabinetValue - shapeHeight;
        //upper cabinets was raised in 3d models
        // hardcode for cabinets that is raised in the model
        // if (startsFromH(shapeDbFeature.sharedFeatureName)) {
        //   posY = posY - 30 + shapeHeight
        // }
      }

      //set upper cabinet height to the level of tall cabinets
      const tallCabinetShapes = CabinetsHelper.getTallCabinetShapes();
      const tallCabinetHighestCabinetValue = parseFloat(CabinetsHelper.getHighestCabinetValue(tallCabinetShapes));
      if (tallCabinetHighestCabinetValue) {
        posY = tallCabinetHighestCabinetValue - shapeHeight;
      }
    }
    return posY
  }

  static getHandles(shape) {
    const lineHandleModels = workspaceControllers.handles.getAllResourcesForTheShape(shape.id);
    //console.log("lineHandleModels", lineHandleModels);

    return lineHandleModels
  }
}
